/**
 * `@define` and `@define-section` parser for the proof. grammar (Track 2.A).
 *
 * From extensibility-design.md §3.6 and §3.7.
 *
 * `@define` inline: `@define hydration(flour, water) = water / flour * 100`
 * `@define-section`: `@define-section @marinade traits=[scalable, shopping-list, timed]`
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import type { DefinitionNode, DefineSectionNode, ScopePath } from "../ast/types.js";
import { parseExpression } from "./expressions.js";

// ---------------------------------------------------------------------------
// @define parser
// ---------------------------------------------------------------------------

export interface DefinitionParseResult {
  node: DefinitionNode | null;
  errors: string[];
}

/**
 * Parse the args portion of a `@define` line.
 *
 * Input examples (after stripping `@define `):
 *   `hydration(flour, water) = water / flour * 100`
 *   `bakers_pct(x, flour) = x / flour * 100`
 */
export function parseDefinition(argsStr: string, scopePath: ScopePath): DefinitionParseResult {
  const trimmed = argsStr.trim();
  const errors: string[] = [];

  // Match: name(params...) = body
  const signatureRe = /^([a-zA-Z_][a-zA-Z0-9_-]*)\s*\(([^)]*)\)\s*=\s*(.+)$/su;
  const m = signatureRe.exec(trimmed);
  if (m === null) {
    errors.push(
      `@define: invalid syntax. Expected: @define name(params...) = expression. Got: '${trimmed}'`,
    );
    return { node: null, errors };
  }

  const name = m[1] ?? "";
  const paramsStr = m[2] ?? "";
  const bodyStr = m[3] ?? "";

  const params = paramsStr
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  const exprResult = parseExpression("=" + bodyStr.trim());
  if (exprResult.node === null) {
    errors.push(...exprResult.errors.map((e) => `@define body: ${e}`));
    return { node: null, errors };
  }

  const node: DefinitionNode = {
    kind: "definition",
    name,
    params,
    body: exprResult.node,
    scopePath,
  };

  return { node, errors };
}

// ---------------------------------------------------------------------------
// @define-section parser
// ---------------------------------------------------------------------------

export interface DefineSectionParseResult {
  node: DefineSectionNode | null;
  errors: string[];
}

/**
 * Parse the args portion of a `@define-section` line.
 *
 * Input examples (after stripping `@define-section `):
 *   `@marinade traits=[scalable, shopping-list, timed]`
 *   `humidity_log traits=[series, charted]`
 */
export function parseSectionDefinition(
  argsStr: string,
  scopePath: ScopePath,
): DefineSectionParseResult {
  const trimmed = argsStr.trim();
  const errors: string[] = [];

  // Match: @sectionname traits=[...]  or  sectionname traits=[...]
  const sectionRe = /^@?([a-zA-Z_][a-zA-Z0-9_-]*)\s+traits=\[([^\]]*)\]/u;
  const m = sectionRe.exec(trimmed);
  if (m === null) {
    errors.push(
      `@define-section: invalid syntax. Expected: @define-section @name traits=[...]. Got: '${trimmed}'`,
    );
    return { node: null, errors };
  }

  const sectionName = m[1] ?? "";
  const traitsStr = m[2] ?? "";
  const traits = traitsStr
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const node: DefineSectionNode = {
    kind: "define-section",
    sectionName,
    traits,
    scopePath,
  };

  return { node, errors };
}
